/* eslint-disable react-refresh/only-export-components */
/**
 * Enhanced Global Search Hook
 *
 * Spotlight-like search combining:
 * - Database records (people, requests, tasks, etc.)
 * - Navigation commands (@pages)
 * - Quick create shortcuts (/commands)
 * - Action commands (>actions)
 * - Category prefix filters (p:, t:, r:, etc.)
 * - Fuzzy matching with typo tolerance
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/hooks/useOrganization";
import { useTenantQueryKey } from "@/hooks/useTenantContext";
import { usePermissions } from "@/hooks/usePermissions";
import { useUserRoles } from "@/hooks/useUserRoles";
import { useCurrentPerson } from "@/hooks/useCurrentPerson";
import { usePersonRoleTags } from "@/hooks/usePersonRoleTags";
import { useAuth } from "@/hooks/useAuth";
import {
  parseSearchQuery,
  searchCommands,
  ALL_COMMANDS,
  CATEGORY_PREFIXES,
  type SearchCommand,
  type CommandCategory,
  calculateFuzzyScore,
} from "@/lib/search-commands";
import type { SearchObjectType, SearchResult } from "@/hooks/useGlobalSearch";
import { isSupabaseUnavailableError } from "@/lib/supabase-compat";
import { STALE_TIME_30_SECONDS, STALE_TIME_1_MINUTE, STALE_TIME_5_MINUTES, STALE_TIME_1_HOUR, STALE_TIME_24_HOURS } from "@/lib/query-constants";

const unavailableSearchIndexOrganizations = new Set<string>();

function isSearchIndexUnavailable(organizationId: string | null | undefined) {
  if (!organizationId) return false;
  return unavailableSearchIndexOrganizations.has(organizationId);
}

function markSearchIndexUnavailable(organizationId: string | null | undefined) {
  if (organizationId) {
    unavailableSearchIndexOrganizations.add(organizationId);
  }
}

// Unified search result type
export type EnhancedSearchResultType = "record" | "command";

export interface EnhancedSearchResult {
  id: string;
  type: EnhancedSearchResultType;
  category: SearchObjectType | CommandCategory;
  label: string;
  description?: string;
  icon: string;
  status?: string;
  score: number;
  // For records
  objectId?: string;
  objectType?: SearchObjectType;
  secondaryLabel?: string;
  // For commands
  command?: SearchCommand;
}

export interface UseEnhancedSearchOptions {
  query: string;
  limit?: number;
  enabled?: boolean;
  activeFilter?: string;
}

export interface EnhancedSearchState {
  results: EnhancedSearchResult[];
  isLoading: boolean;
  isEmpty: boolean;
  hasRecords: boolean;
  hasCommands: boolean;
  activePrefix: string | null;
  prefixMeta: { label: string; icon: string } | null;
}

export function useEnhancedSearch({
  query,
  limit = 25,
  enabled = true,
  activeFilter,
}: UseEnhancedSearchOptions): EnhancedSearchState {
  const { organization } = useOrganization();
  const tenantQueryKey = useTenantQueryKey();
  const { canAccessModule, isOrgAdmin, isLoading: permissionsLoading } = usePermissions();
  const { isPlatformAdmin, isStaff: hasStaffRole, isLoading: rolesLoading } = useUserRoles();
  const { data: currentPerson, isLoading: personLoading } = useCurrentPerson();
  const { data: currentPersonRoleTags = [] } = usePersonRoleTags(currentPerson?.id);
  const { user } = useAuth();

  const isStaff = hasStaffRole || currentPersonRoleTags.includes("Staff");

  // Parse the query for prefixes, respecting active filter if set
  const { prefix: parsedPrefix, searchTerm, filteredTypes: parsedFilteredTypes } = useMemo(
    () => parseSearchQuery(query),
    [query]
  );

  // If active filter is set (and not "all"), override the prefix
  // "activeFilter" comes from the UI tabs (e.g. "p:", "r:", etc.)
  const effectivePrefix = useMemo(() => {
    if (activeFilter && activeFilter !== "all" && activeFilter !== "") {
      // Find the prefix for this filter ID
      // This logic depends on how activeFilter is passed. 
      // If activeFilter is passed as the tab ID (e.g. "people"), we need to map it.
      // But let's assume the caller passes the mapped prefix or handles it.
      // Actually, let's just use the activeFilter as the prefix if it looks like one.
      return activeFilter;
    }
    return parsedPrefix;
  }, [activeFilter, parsedPrefix]);

  // Re-derive filtered types if we overrode the prefix
  const { filteredTypes } = useMemo(() => {
    if (effectivePrefix !== parsedPrefix) {
      // We need to re-parse with the forced prefix
      // Construct a fake query like "p: query"
      return parseSearchQuery(effectivePrefix + query);
    }
    return { filteredTypes: parsedFilteredTypes };
  }, [effectivePrefix, parsedPrefix, query, parsedFilteredTypes]);

  const prefix = effectivePrefix;

  // Determine if we should search commands or records
  const isCommandSearch = prefix === "/" || prefix === ">" || prefix === "@";
  const isRecordSearch = !isCommandSearch && filteredTypes !== null && filteredTypes[0] !== "navigation" && filteredTypes[0] !== "command" && filteredTypes[0] !== "action";
  const isMixedSearch = !prefix; // No prefix = search both

  // Search commands (synchronous, client-side)
  const commandResults = useMemo<EnhancedSearchResult[]>(() => {
    if (!enabled || permissionsLoading || rolesLoading) return [];

    // Only search commands if:
    // - It's a command search (/, >, @)
    // - Or it's a mixed search with a query
    if (!isCommandSearch && !isMixedSearch) return [];

    const effectiveQuery = isCommandSearch ? searchTerm : query;
    if (effectiveQuery.length < 1) return [];

    // Filter commands based on prefix
    let commandsToSearch = ALL_COMMANDS;
    if (prefix === "@") {
      commandsToSearch = ALL_COMMANDS.filter(c => c.category === "navigation");
    } else if (prefix === "/") {
      commandsToSearch = ALL_COMMANDS.filter(c => c.category === "create" || c.category === "action");
    } else if (prefix === ">") {
      commandsToSearch = ALL_COMMANDS.filter(c => c.category === "action");
    }

    const results = searchCommands(effectiveQuery, commandsToSearch, {
      canAccessModule,
      isStaff: isStaff ?? false,
      isAdmin: isPlatformAdmin,
      isOrgAdmin,
    });

    // Convert to unified result type
    return results.slice(0, isMixedSearch ? 5 : limit).map((cmd): EnhancedSearchResult => ({
      id: cmd.id,
      type: "command",
      category: cmd.category,
      label: cmd.label,
      description: cmd.description,
      icon: cmd.icon,
      score: cmd.score,
      command: cmd,
    }));
  }, [
    enabled,
    permissionsLoading,
    rolesLoading,
    isCommandSearch,
    isMixedSearch,
    searchTerm,
    query,
    prefix,
    canAccessModule,
    isStaff,
    isPlatformAdmin,
    isOrgAdmin,
    limit,
  ]);

  // Search database records
  const recordsQuery = useQuery({
    queryKey: [...tenantQueryKey, "enhanced-search", organization?.id, searchTerm || query, filteredTypes],
    queryFn: async () => {
      if (!organization?.id) return [];
      if (isSearchIndexUnavailable(organization?.id)) return [];

      const effectiveQuery = (isRecordSearch ? searchTerm : query).trim();
      if (effectiveQuery.length < 2) return [];

      let searchQuery = supabase
        .from("search_index")
        .select("*")
        .eq("organization_id", organization.id)
        .order("source_updated_at", { ascending: false })
        .limit(isMixedSearch ? limit - 5 : limit);

      // Apply type filter if prefix was used
      if (isRecordSearch && filteredTypes && filteredTypes.length > 0) {
        searchQuery = searchQuery.in("object_type", filteredTypes);
      }

      // Use full-text search if query has multiple words, otherwise use ILIKE
      if (effectiveQuery.includes(' ')) {
        const tsQuery = effectiveQuery.split(' ').filter(Boolean).join(' & ');
        searchQuery = searchQuery.textSearch('search_vector', tsQuery, {
          type: 'plain',
          config: 'english',
        });
      } else {
        searchQuery = searchQuery.or(
          `primary_label.ilike.%${effectiveQuery}%,secondary_label.ilike.%${effectiveQuery}%`
        );
      }

      const { data, error } = await searchQuery;
      if (error) {
        if (isSupabaseUnavailableError(error)) {
          markSearchIndexUnavailable(organization.id);
          return [];
        }
        throw error;
      }
      return data as SearchResult[];
    },
    enabled:
      enabled &&
      !!organization?.id &&
      (isRecordSearch || isMixedSearch) &&
      (searchTerm || query).trim().length >= 2 &&
      !isSearchIndexUnavailable(organization?.id),
    staleTime: STALE_TIME_30_SECONDS,
  });

  // Convert database results to unified type with scoring
  const recordResults = useMemo<EnhancedSearchResult[]>(() => {
    if (!recordsQuery.data) return [];

    const effectiveQuery = (isRecordSearch ? searchTerm : query).trim();

    return recordsQuery.data.map((record): EnhancedSearchResult => {
      // Calculate score based on match quality
      const labelScore = calculateFuzzyScore(effectiveQuery, record.primary_label);
      const secondaryScore = record.secondary_label
        ? calculateFuzzyScore(effectiveQuery, record.secondary_label) * 0.8
        : 0;
      const score = Math.max(labelScore, secondaryScore);

      return {
        id: record.id,
        type: "record",
        category: record.object_type,
        label: record.primary_label,
        description: record.secondary_label || undefined,
        secondaryLabel: record.secondary_label || undefined,
        icon: getObjectTypeIcon(record.object_type),
        status: record.status || undefined,
        score,
        objectId: record.object_id,
        objectType: record.object_type,
      };
    });
  }, [recordsQuery.data, searchTerm, query, isRecordSearch]);

  // Combine and sort all results
  const combinedResults = useMemo<EnhancedSearchResult[]>(() => {
    if (isCommandSearch) {
      return commandResults;
    }
    if (isRecordSearch) {
      return recordResults;
    }

    // Mixed search: combine with commands first, then records
    const allResults = [...commandResults, ...recordResults];

    // Sort by score descending
    allResults.sort((a, b) => b.score - a.score);

    return allResults.slice(0, limit);
  }, [isCommandSearch, isRecordSearch, commandResults, recordResults, limit]);

  // Prefix metadata for UI hints
  const prefixMeta = prefix ? CATEGORY_PREFIXES[prefix] : null;

  return {
    results: combinedResults,
    isLoading: recordsQuery.isLoading,
    isEmpty: combinedResults.length === 0 && !recordsQuery.isLoading,
    hasRecords: recordResults.length > 0,
    hasCommands: commandResults.length > 0,
    activePrefix: prefix,
    prefixMeta,
  };
}

// Helper to get icon name for object types
function getObjectTypeIcon(objectType: SearchObjectType): string {
  const icons: Record<SearchObjectType, string> = {
    person: "User",
    household: "Home",
    request: "MessageSquare",
    task: "CheckSquare",
    project: "FolderKanban",
    contribution: "DollarSign",
    fund: "Wallet",
    asset: "Package",
    campaign: "Target",
    pledge: "HandHeart",
    event: "Calendar",
    service: "Church",
  };
  return icons[objectType] || "Search";
}

// Hook for suggested/recent items when search is empty
export function useSearchSuggestions() {
  const { organization } = useOrganization();
  const tenantQueryKey = useTenantQueryKey();
  const { user } = useAuth();

  // Recent items the user has interacted with
  const recentItemsQuery = useQuery({
    queryKey: [...tenantQueryKey, "search-suggestions-recent", organization?.id, user?.id],
    queryFn: async () => {
      if (!organization?.id) return [];
      if (isSearchIndexUnavailable(organization?.id)) return [];

      const { data, error } = await supabase
        .from("search_index")
        .select("*")
        .eq("organization_id", organization.id)
        .order("source_updated_at", { ascending: false })
        .limit(8);

      if (error) {
        if (isSupabaseUnavailableError(error)) {
          markSearchIndexUnavailable(organization.id);
          return [];
        }
        throw error;
      }
      return data as SearchResult[];
    },
    enabled: !!organization?.id && !isSearchIndexUnavailable(organization?.id),
    staleTime: STALE_TIME_1_MINUTE,
  });

  // My assigned items (tasks, requests, projects, contributions)
  const myItemsQuery = useQuery({
    queryKey: [...tenantQueryKey, "search-suggestions-mine", organization?.id, user?.id],
    queryFn: async () => {
      if (!organization?.id || !user?.id) return [];
      if (isSearchIndexUnavailable(organization?.id)) return [];

      const { data, error } = await supabase
        .from("search_index")
        .select("*")
        .eq("organization_id", organization.id)
        .eq("assignee_id", user.id)
        .in("object_type", ["task", "request", "project", "contribution"])
        .in("status", ["open", "pending", "in_progress", "active"])
        .order("source_updated_at", { ascending: false })
        .limit(8);

      if (error) {
        if (isSupabaseUnavailableError(error)) {
          markSearchIndexUnavailable(organization.id);
          return [];
        }
        throw error;
      }
      return data as SearchResult[];
    },
    enabled: !!organization?.id && !!user?.id && !isSearchIndexUnavailable(organization?.id),
    staleTime: STALE_TIME_1_MINUTE,
  });

  const recentItems = useMemo<EnhancedSearchResult[]>(() => {
    if (!recentItemsQuery.data) return [];
    return recentItemsQuery.data.map((record): EnhancedSearchResult => ({
      id: record.id,
      type: "record",
      category: record.object_type,
      label: record.primary_label,
      description: record.secondary_label || undefined,
      secondaryLabel: record.secondary_label || undefined,
      icon: getObjectTypeIcon(record.object_type),
      status: record.status || undefined,
      score: 0,
      objectId: record.object_id,
      objectType: record.object_type,
    }));
  }, [recentItemsQuery.data]);

  const myItems = useMemo<EnhancedSearchResult[]>(() => {
    if (!myItemsQuery.data) return [];
    return myItemsQuery.data.map((record): EnhancedSearchResult => ({
      id: record.id,
      type: "record",
      category: record.object_type,
      label: record.primary_label,
      description: record.secondary_label || undefined,
      secondaryLabel: record.secondary_label || undefined,
      icon: getObjectTypeIcon(record.object_type),
      status: record.status || undefined,
      score: 0,
      objectId: record.object_id,
      objectType: record.object_type,
    }));
  }, [myItemsQuery.data]);

  // Quick commands (always available)
  const quickCommands = useMemo<EnhancedSearchResult[]>(() => {
    const commands = ALL_COMMANDS.filter(c =>
      c.id === "create-task" ||
      c.id === "create-request" ||
      c.id === "create-person" ||
      c.id === "create-contribution" ||
      c.id === "nav-tasks" ||
      c.id === "nav-calendar" ||
      c.id === "nav-people" ||
      c.id === "nav-requests"
    );
    return commands.map((cmd): EnhancedSearchResult => ({
      id: cmd.id,
      type: "command",
      category: cmd.category,
      label: cmd.label,
      description: cmd.description,
      icon: cmd.icon,
      score: 0,
      command: cmd,
    }));
  }, []);

  return {
    recentItems,
    myItems,
    quickCommands,
    isLoading: recentItemsQuery.isLoading || myItemsQuery.isLoading,
  };
}

// Export prefix info for UI
export { CATEGORY_PREFIXES } from "@/lib/search-commands";